import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  POSITION_GROUPS,
  groupForPosition,
  MV_PRESETS,
  SALARY_PRESETS,
  parseMoneyShorthand,
  AGE_BOUNDS,
} from "./lmFilterMeta";

// Mobile filters bottom sheet — V2 rebuild 1:1 from artboard 8b: grab handle,
// "Filters" + Reset, position group→detail pills, dual-thumb age slider,
// market-value presets (from the profile — league levels dropped in Round 2),
// salary presets + custom band row, the two toggle rows with descriptions, and
// the full-width "Show N listings" CTA. Same state/setters as the page —
// presentation only.

// Dual-range slider CSS (two overlapped native ranges; thumbs 20px white per
// artboard). Injected once.
let sliderCssInjected = false;
const ensureSliderCss = () => {
  if (sliderCssInjected || typeof document === "undefined") return;
  if (document.getElementById("lm-range-style")) {
    sliderCssInjected = true;
    return;
  }
  const style = document.createElement("style");
  style.id = "lm-range-style";
  style.textContent = [
    ".lm-range{-webkit-appearance:none;appearance:none;position:absolute;left:0;right:0;top:0;height:20px;width:100%;background:transparent;pointer-events:none;margin:0}",
    ".lm-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;pointer-events:auto;width:20px;height:20px;border-radius:50%;background:#fff;box-shadow:0 1px 4px rgba(16,24,40,.3);border:none;cursor:pointer}",
    ".lm-range::-moz-range-thumb{pointer-events:auto;width:20px;height:20px;border-radius:50%;background:#fff;box-shadow:0 1px 4px rgba(16,24,40,.3);border:none;cursor:pointer}",
    ".lm-range::-moz-range-track{background:transparent}",
  ].join("");
  document.head.appendChild(style);
  sliderCssInjected = true;
};

const pill = (active, extra = "") =>
  `rounded-full font-semibold ${extra} ${
    active
      ? "border-[1.5px] border-[#2283FF] bg-[#F0F7FF] text-[#1466D8] font-bold"
      : "border border-[#DFE6EF] bg-white text-[#4A5C72]"
  }`;

// Section label: 12px/600 #33475E with a muted hint.
const Label = ({ children, hint }) => (
  <div className="text-[12px] font-semibold text-[#33475E] mb-2">
    {children}
    {hint && <span className="font-medium text-[#98A5B8]"> · {hint}</span>}
  </div>
);

const LmFilterSheet = ({
  open,
  onClose,
  count,
  listingType,
  mvMin,
  setMvMin,
  mvMax,
  setMvMax,
  salaryMax,
  setSalaryMax,
  readyToFly,
  setReadyToFly,
  shortTerm,
  setShortTerm,
  country,
  setCountry,
  countries,
  position,
  setPosition,
  ageMin,
  setAgeMin,
  ageMax,
  setAgeMax,
}) => {
  const [group, setGroup] = useState(() => groupForPosition(position));
  const [countryQuery, setCountryQuery] = useState("");
  const [salaryCustomMin, setSalaryCustomMin] = useState("");
  const [salaryCustomMax, setSalaryCustomMax] = useState("");
  useEffect(() => {
    if (open) setGroup(groupForPosition(position) || group || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    ensureSliderCss();
  }, []);
  if (!open) return null;

  const activeGroup = POSITION_GROUPS.find((g) => g.key === group);
  const lo = ageMin === "" || ageMin == null ? AGE_BOUNDS.min : Number(ageMin);
  const hi = ageMax === "" || ageMax == null ? AGE_BOUNDS.max : Number(ageMax);
  const pct = (v) => ((v - AGE_BOUNDS.min) / (AGE_BOUNDS.max - AGE_BOUNDS.min)) * 100;
  const mvActive = (p) =>
    String(mvMin ?? "") === String(p.min ?? "") && String(mvMax ?? "") === String(p.max ?? "");

  const reset = () => {
    setPosition("");
    setGroup("");
    setAgeMin("");
    setAgeMax("");
    setMvMin("");
    setMvMax("");
    setSalaryMax(null);
    setSalaryCustomMin("");
    setSalaryCustomMax("");
    setCountry("");
    setReadyToFly(false);
    setShortTerm(false);
  };

  const applySalaryCustom = (minRaw, maxRaw) => {
    setSalaryCustomMin(minRaw);
    setSalaryCustomMax(maxRaw);
    const max = parseMoneyShorthand(maxRaw);
    // The BE's only salary param is salary_max (band-min within budget) — the
    // custom row applies its upper bound; see TODO.md for the salary_min gap.
    setSalaryMax(max || null);
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end bg-[rgba(16,26,40,0.5)]" role="dialog" aria-modal="true">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full bg-white rounded-t-[22px] max-h-[92vh] flex flex-col px-[18px] pt-2.5 pb-10 gap-4 overflow-hidden">
        {/* Grab handle */}
        <span className="w-[38px] h-[4.5px] rounded-full bg-[#D5DDE8] self-center flex-shrink-0" aria-hidden="true" />
        {/* Header: Filters · Reset */}
        <div className="flex items-center flex-shrink-0">
          <h2 className="text-[17px] font-bold text-[#14263D]">Filters</h2>
          <button onClick={reset} className="ml-auto text-[12.5px] font-semibold text-[#1466D8]">
            Reset
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col gap-4 min-h-0">
          {/* Position — group, then detail (players only) */}
          {listingType === "player" && (
            <div>
              <Label hint="group, then detail">Position</Label>
              <div className="flex flex-wrap gap-[7px]">
                {POSITION_GROUPS.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => {
                      if (group === g.key) {
                        setGroup("");
                        setPosition("");
                      } else {
                        setGroup(g.key);
                        if (g.details.length === 1) setPosition(g.details[0].be);
                        else if (!g.details.some((d) => d.be === position)) setPosition("");
                      }
                    }}
                    className={pill(group === g.key, "px-[13px] py-[7px] text-[12px]")}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              {activeGroup && activeGroup.details.length > 1 && (
                <div className="flex flex-wrap gap-[7px] mt-2 px-2.5 py-[9px] bg-[#F8FAFC] border border-[#EEF1F6] rounded-xl">
                  {activeGroup.details.map((d) => (
                    <button
                      key={d.code}
                      type="button"
                      onClick={() => setPosition(position === d.be ? "" : d.be)}
                      className={pill(position === d.be, "px-[11px] py-[5px] text-[11.5px]")}
                    >
                      {d.code}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Age — dual-thumb slider (8b) */}
          <div>
            <div className="flex text-[12px] font-semibold text-[#33475E] mb-3">
              Age
              <span className="ml-auto text-[#1466D8]">
                {lo} – {hi}
              </span>
            </div>
            <div className="relative h-5 mx-2">
              <span className="absolute top-2 left-0 right-0 h-1 rounded-full bg-[#E4E9F1]" />
              <span
                className="absolute top-2 h-1 rounded-full bg-[#2283FF]"
                style={{ left: `${pct(Math.min(lo, hi))}%`, right: `${100 - pct(Math.max(lo, hi))}%` }}
              />
              <input
                type="range"
                className="lm-range"
                min={AGE_BOUNDS.min}
                max={AGE_BOUNDS.max}
                value={lo}
                aria-label="Minimum age"
                onChange={(e) => {
                  const v = Math.min(Number(e.target.value), hi);
                  setAgeMin(v === AGE_BOUNDS.min ? "" : String(v));
                }}
              />
              <input
                type="range"
                className="lm-range"
                min={AGE_BOUNDS.min}
                max={AGE_BOUNDS.max}
                value={hi}
                aria-label="Maximum age"
                onChange={(e) => {
                  const v = Math.max(Number(e.target.value), lo);
                  setAgeMax(v === AGE_BOUNDS.max ? "" : String(v));
                }}
              />
            </div>
          </div>

          {/* Market value — presets from the profile (players only) */}
          {listingType === "player" && (
            <div>
              <Label hint="from the player profile">Market value</Label>
              <div className="flex flex-wrap gap-[7px]">
                {MV_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      if (mvActive(p)) {
                        setMvMin("");
                        setMvMax("");
                      } else {
                        setMvMin(p.min == null ? "" : String(p.min));
                        setMvMax(p.max == null ? "" : String(p.max));
                      }
                    }}
                    className={pill(mvActive(p), "px-[13px] py-[7px] text-[12px]")}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="text-[10.5px] text-[#98A5B8] mt-1.5">
                One number, comparable across every league, no tier taxonomy.
              </p>
            </div>
          )}

          {/* Salary band — presets or your own range */}
          <div>
            <Label hint="presets or your own range">Salary band</Label>
            <div className="flex flex-wrap gap-[7px]">
              {SALARY_PRESETS.slice(0, 4).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSalaryMax(salaryMax === p.max ? null : p.max)}
                  className={pill(salaryMax != null && salaryMax === p.max, "px-[13px] py-[7px] text-[12px]")}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2 border border-[#DFE6EF] rounded-xl px-3 py-[9px]">
              <span className="text-[12px] font-semibold text-[#8A98AC]">Custom</span>
              <input
                value={salaryCustomMin}
                onChange={(e) => applySalaryCustom(e.target.value, salaryCustomMax)}
                placeholder="25k"
                inputMode="text"
                className="flex-1 min-w-0 border border-[#E4E9F1] bg-[#F8FAFC] rounded-[9px] px-2.5 py-[7px] text-[12.5px] font-semibold text-[#14263D] text-center"
              />
              <span className="text-[12px] text-[#8A98AC]">–</span>
              <input
                value={salaryCustomMax}
                onChange={(e) => applySalaryCustom(salaryCustomMin, e.target.value)}
                placeholder="45k"
                inputMode="text"
                className="flex-1 min-w-0 border border-[#E4E9F1] bg-[#F8FAFC] rounded-[9px] px-2.5 py-[7px] text-[12.5px] font-semibold text-[#14263D] text-center"
              />
              <span className="text-[12px] font-semibold text-[#4A5C72] whitespace-nowrap">EUR / mo</span>
            </div>
          </div>

          {/* Country (kept from the platform — the artboard sheet is silent) */}
          <div>
            <Label>Country</Label>
            <input
              value={countryQuery}
              onChange={(e) => setCountryQuery(e.target.value)}
              placeholder="Search countries…"
              className="w-full min-h-[44px] bg-[#F8FAFC] border border-[#E4E9F1] rounded-xl px-3 text-[13px] mb-2"
            />
            <div className="max-h-[180px] overflow-y-auto flex flex-col">
              {country && (
                <button onClick={() => setCountry("")} className="text-left min-h-[40px] px-2 rounded-md text-[13px] text-[#C4402F]">
                  Clear ({country})
                </button>
              )}
              {countries
                .filter((c) => (countryQuery ? (c.name || "").toLowerCase().includes(countryQuery.toLowerCase()) : true))
                .slice(0, 40)
                .map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCountry(c.name)}
                    className={`flex items-center gap-2 text-left min-h-[40px] px-2 rounded-md text-[13px] truncate ${
                      country === c.name ? "bg-[#F0F7FF] text-[#1466D8] font-semibold" : ""
                    }`}
                  >
                    {c.image_url && <img src={c.image_url} alt="" className="w-5 h-3.5 object-contain flex-none" referrerPolicy="no-referrer" />}
                    <span className="truncate">{c.name}</span>
                  </button>
                ))}
            </div>
          </div>

          {/* Toggle rows (8b): title + description + switch */}
          <div className="flex items-center gap-2.5 border border-[#E4E9F1] rounded-xl px-3.5 py-3">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#14263D]">Ready to Fly only</div>
              <div className="text-[11px] text-[#8A98AC] mt-px">Can relocate abroad immediately</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={readyToFly}
              aria-label="Ready to Fly only"
              onClick={() => setReadyToFly(!readyToFly)}
              className={`relative w-10 h-6 rounded-full flex-none transition-colors ${readyToFly ? "bg-[#2283FF]" : "bg-[#D5DDE8]"}`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,.25)] transition-all ${
                  readyToFly ? "right-0.5" : "left-0.5"
                }`}
              />
            </button>
          </div>
          <div className="flex items-center gap-2.5 border border-[#E4E9F1] rounded-xl px-3.5 py-3">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#14263D]">Short-term contracts</div>
              <div className="text-[11px] text-[#8A98AC] mt-px">Open to deals under 12 months</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={shortTerm}
              aria-label="Short-term contracts"
              onClick={() => setShortTerm(!shortTerm)}
              className={`relative w-10 h-6 rounded-full flex-none transition-colors ${shortTerm ? "bg-[#2283FF]" : "bg-[#D5DDE8]"}`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,.25)] transition-all ${
                  shortTerm ? "right-0.5" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={onClose}
          className="flex-shrink-0 w-full text-center bg-[#2283FF] text-white rounded-xl py-3.5 text-[14px] font-semibold shadow-[0_2px_6px_rgba(34,131,255,.35)]"
        >
          Show {count} listing{count === 1 ? "" : "s"}
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default LmFilterSheet;
